import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { MobileShell } from "@/components/MobileShell";
import { Wordmark, Button, Spinner } from "@/components/kit";
import { api, formatApiError } from "@/lib/api";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setError("No verification token provided."); return; }
    api.post("/auth/verify-email", { token })
      .then(() => setState("success"))
      .catch((err) => { setState("error"); setError(formatApiError(err.response?.data?.detail) || err.message); });
  }, [token]);

  return (
    <MobileShell>
      <div className="px-7 pt-16 lg:pt-12 pb-10 min-h-screen flex flex-col items-center text-center">
        <Wordmark className="lg:hidden" />
        <div className="mt-20">
          {state === "verifying" && (
            <div className="flex flex-col items-center gap-4" data-testid="verify-loading">
              <Spinner />
              <p className="font-sans text-sm text-[#5F6D66]">Verifying your email…</p>
            </div>
          )}
          {state === "success" && (
            <div className="animate-rise" data-testid="verify-success">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#F1EEE7] flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-[#3D6B56]" strokeWidth={1.6} />
              </div>
              <h1 className="font-serif text-2xl text-[#1F2B26] mt-6">Email verified</h1>
              <p className="font-sans text-sm text-[#5F6D66] mt-3">Thank you. Your account is now verified.</p>
              <Button className="mt-8" onClick={() => navigate("/app/discover")} data-testid="verify-continue-btn">Continue</Button>
            </div>
          )}
          {state === "error" && (
            <div className="animate-rise" data-testid="verify-error">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#F3EBE1] flex items-center justify-center">
                <XCircle className="h-7 w-7 text-[#9B4B45]" strokeWidth={1.6} />
              </div>
              <h1 className="font-serif text-2xl text-[#1F2B26] mt-6">Verification failed</h1>
              <p className="font-sans text-sm text-[#5F6D66] mt-3">{error}</p>
              <Button variant="outline" className="mt-8" onClick={() => navigate("/app/discover")} data-testid="verify-back-btn">Go to app</Button>
            </div>
          )}
        </div>
      </div>
    </MobileShell>
  );
}
